import { Button, Dialog, DialogButtons } from '@st/theme'
import ReactDOM from 'react-dom/client'
import { Cause, Data, Effect } from 'effect'

import '@st/theme/fonts.css'
import './styles/globals.css'

import { App } from './app'
import { initSentry } from './init-sentry'
import { buildAppVersion } from '@st/util/app-version'
import { AppType } from '@features/app-type'
import { makeSTAppStore, StoreProvider } from './store'
import { envVar } from './env'
import { BrowserResourceBoundary } from './features/browser-resources/browser-resource-boundary'
import { PageTextProvider } from './st-doc-viewer/page-text-boundary'

function bootApp() {
  const clientVersion = buildAppVersion(
    envVar('NEXT_PUBLIC_BUILD_TIME'),
    envVar('NEXT_PUBLIC_BUILD_SHA')
  )
  console.log(`Booting st-app version ${clientVersion}`)
  initSentry()
}

type BootConfig = {
  appType: AppType
  browserOrigin: string
  devMode: boolean
  enableDevtools: boolean
}

class LocalStorageUnavailable extends Data.TaggedError('LocalStorageUnavailable')<{
  cause: unknown
}> {}

// Root-only: decide which app to boot exactly once from URL origin.
// Feature/modules must not sniff `window.location` for app type; treat it as a boot flag.
function getBootConfig(): Effect.Effect<BootConfig> {
  return Effect.gen(function* () {
    const href = yield* Effect.sync(() => window.location.href)
    const url = new URL(href)
    const appType = url.origin == envVar('NEXT_PUBLIC_CLIENTS_APP_URL') ? 'client' : 'organization'

    return {
      appType,
      browserOrigin: url.origin,
      devMode: appType == 'organization' ? yield* readDevModeAtBoot : false,
      enableDevtools: '__REDUX_DEVTOOLS_EXTENSION__' in window
    }
  })
}

const readDevModeAtBoot = Effect.try({
  try: () => localStorage.getItem('dev') == 'true',
  catch: (cause) => new LocalStorageUnavailable({ cause })
}).pipe(Effect.catchTag('LocalStorageUnavailable', () => Effect.succeed(false)))

const appFiber = Effect.runFork(
  Effect.scoped(
    Effect.gen(function* () {
      // The host owns the React root; the running application owns its store,
      // services and fibers. Failure releases that application before rendering
      // the terminal dialog, which must not depend on the failed store.
      const root = yield* Effect.sync(() => ReactDOM.createRoot(document.getElementById('root')!))
      yield* Effect.addFinalizer(() => Effect.sync(() => root.unmount()))

      yield* Effect.scoped(
        Effect.gen(function* () {
          yield* Effect.sync(bootApp)
          const boot = yield* getBootConfig()
          const appStore = yield* makeSTAppStore({
            appType: boot.appType,
            browserOrigin: boot.browserOrigin,
            devMode: boot.devMode,
            enableDevtools: boot.enableDevtools
          })
          yield* Effect.addFinalizer(() => Effect.sync(() => root.render(null)))
          yield* Effect.sync(() =>
            root.render(
              // Strict mode is disabled because of the PDFTron/Apryse viewer.
              <PageTextProvider value={appStore.resolvePageText}>
                <BrowserResourceBoundary capture={appStore.browserResources.capture}>
                  <StoreProvider store={appStore.store}>
                    <App appType={boot.appType} devMode={boot.devMode} />
                  </StoreProvider>
                </BrowserResourceBoundary>
              </PageTextProvider>
            )
          )
          return yield* appStore.store.awaitDefect()
        })
      ).pipe(
        Effect.catchCause((cause) => {
          // HMR and host teardown are cancellation, not application failure.
          if (Cause.hasInterruptsOnly(cause)) return Effect.failCause(cause)
          return Effect.gen(function* () {
            yield* Effect.logError(cause)
            yield* Effect.sync(() =>
              root.render(
                <div className="fixed inset-0 flex items-center justify-center bg-stone-100">
                  <Dialog
                    title="Something went wrong"
                    subtitle="This session cannot continue. Reload to start again. Unsaved changes may be lost."
                    buttons={
                      <DialogButtons>
                        <Button variant="primary" onClick={() => window.location.reload()}>
                          Reload
                        </Button>
                      </DialogButtons>
                    }
                  />
                </div>
              )
            )
            return yield* Effect.never
          })
        })
      )
    })
  )
)

if (import.meta.hot) {
  import.meta.hot.dispose(() => appFiber.interruptUnsafe())
}
